import React from 'react';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import styles from './richtexteditor.module.css';
import { useFocusTrap } from '../../hooks/useFocusTrap';

// ── Legacy markdown-рендерер ──────────────────────────────────────────
// Старые сообщения хранились в Markdown. Новые — в HTML (TipTap).
// renderMarkdown оставлен для обратной совместимости со старым контентом.
export function renderMarkdown(text: string): string {
  if (!text?.trim()) return '';

  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const inline = (s: string): string => {
    s = esc(s);
    s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
    return s;
  };

  const lines = text.split('\n');
  let html = '';
  let inUl = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isList  = /^[-•] /.test(line);
    const isQuote = line.startsWith('> ');
    const isEmpty = line.trim() === '';

    if (!isList && inUl) { html += '</ul>'; inUl = false; }

    if (isQuote) {
      html += `<blockquote>${inline(line.slice(2))}</blockquote>`;
    } else if (isList) {
      const content = line.replace(/^[-•] /, '');
      if (!inUl) { html += '<ul>'; inUl = true; }
      html += `<li>${inline(content)}</li>`;
    } else if (isEmpty) {
      html += '<br>';
    } else {
      html += `<span class="${styles.mdLine}">${inline(line)}</span><br>`;
    }
  }

  if (inUl) html += '</ul>';
  return html;
}

// Определяем, является ли контент HTML (новый формат) или Markdown (legacy).
function looksLikeHtml(text: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

/**
 * Безопасный рендер контента сообщения: HTML → санитизация DOMPurify,
 * legacy Markdown → renderMarkdown + санитизация. Всегда чистый HTML.
 */
export function renderRich(text: string): string {
  if (!text?.trim()) return '';
  const raw = looksLikeHtml(text) ? text : renderMarkdown(text);
  return DOMPurify.sanitize(raw, {
    ADD_TAGS: ['details', 'summary'],
    ADD_ATTR: ['target', 'style', 'data-type', 'data-checked', 'data-color', 'type', 'checked', 'disabled', 'width', 'data-align'],
    // open вырезаем — спойлеры у читателя свёрнуты
    FORBID_ATTR: ['open'],
  });
}

// ── Лайтбокс: просмотр изображений с зумом, паном и галереей ──────────
// Управление: колесо — зум к курсору; drag — панорамирование; двойной
// клик — «вписать ↔ 100%»; ←/→ — переход по галерее; Esc/фон — закрыть.
const LB_MIN_SCALE = 0.2;
const LB_MAX_SCALE = 8;

export function ImageLightbox({ src, list, onClose }: {
  src: string;
  /** Галерея: полный список изображений блока (src обычно входит в него). */
  list?: string[];
  onClose: () => void;
}) {
  const gallery = list && list.length > 1 ? list : null;
  const [index, setIndex] = React.useState(() => Math.max(0, (gallery ?? [src]).indexOf(src)));
  const current = gallery ? gallery[index] : src;

  // Трансформация картинки: масштаб + смещение в px экрана.
  // scale=1 — «вписать в экран» (max-width/max-height из CSS).
  const [t, setT] = React.useState({ scale: 1, x: 0, y: 0 });
  const [dragging, setDragging] = React.useState(false);
  const dragRef    = React.useRef<{ startX: number; startY: number; baseX: number; baseY: number; moved: boolean } | null>(null);
  const imgRef     = React.useRef<HTMLImageElement>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);

  const reset = React.useCallback(() => setT({ scale: 1, x: 0, y: 0 }), []);
  const fitted = t.scale === 1 && t.x === 0 && t.y === 0;

  const goto = React.useCallback((di: number) => {
    if (!gallery) return;
    setIndex(i => (i + di + gallery.length) % gallery.length);
    setT({ scale: 1, x: 0, y: 0 });
  }, [gallery]);

  // Зум с сохранением точки (px, py — экранные координаты) на месте
  const zoomAt = React.useCallback((factor: number, px?: number, py?: number) => {
    const img = imgRef.current;
    setT(prev => {
      const next = Math.min(LB_MAX_SCALE, Math.max(LB_MIN_SCALE, prev.scale * factor));
      if (next === prev.scale) return prev;
      if (!img) return { ...prev, scale: next };
      const rect = img.getBoundingClientRect();
      const cx = px ?? rect.left + rect.width / 2;
      const cy = py ?? rect.top + rect.height / 2;
      const rcx = rect.left + rect.width / 2;
      const rcy = rect.top + rect.height / 2;
      const k = 1 - next / prev.scale;
      return { scale: next, x: prev.x + (cx - rcx) * k, y: prev.y + (cy - rcy) * k };
    });
  }, []);

  // Фокус внутри просмотра и возврат на картинку, по которой кликнули;
  // заодно панель тикета под ним не закроется по Esc (она проверяет,
  // что поверх неё нет другого окна)
  useFocusTrap(overlayRef);

  // Клавиатура: Esc — закрыть, стрелки — галерея
  React.useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      else if (e.key === 'ArrowLeft')  goto(-1);
      else if (e.key === 'ArrowRight') goto(1);
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose, goto]);

  // Колесо: зум к курсору. Слушатель — не passive, чтобы страница под
  // оверлеем не скроллилась (React onWheel этого не гарантирует).
  React.useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  // Панорамирование мышью
  const onImgMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: t.x, baseY: t.y, moved: false };
    setDragging(true);
  };
  React.useEffect(() => {
    if (!dragging) return;
    const mm = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
      setT(prev => ({ ...prev, x: d.baseX + dx, y: d.baseY + dy }));
    };
    const mu = () => setDragging(false);
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', mu);
    return () => { window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', mu); };
  }, [dragging]);

  // Двойной клик: «вписать ↔ 100%» (реальный размер) к точке клика
  const toggleActualSize = React.useCallback((px?: number, py?: number) => {
    const img = imgRef.current;
    if (!img) return;
    if (!fitted) { reset(); return; }
    const target = Math.max(1.5, (img.naturalWidth || img.clientWidth) / Math.max(1, img.clientWidth));
    zoomAt(target, px, py);
  }, [fitted, reset, zoomAt]);

  const openOriginal = () => window.open(current, '_blank', 'noopener');

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр изображения"
      tabIndex={-1}
      className={styles.imgLightbox}
      onClick={e => { e.stopPropagation(); onClose(); }}
    >
      <img
        ref={imgRef}
        src={current}
        alt=""
        draggable={false}
        onClick={e => e.stopPropagation()}
        onMouseDown={onImgMouseDown}
        onDoubleClick={e => { e.stopPropagation(); toggleActualSize(e.clientX, e.clientY); }}
        style={{
          transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      />

      {/* Панель управления */}
      <div className={styles.lbToolbar} onClick={e => e.stopPropagation()}>
        <button type="button" className={styles.lbBtn} title="Отдалить" onClick={() => zoomAt(1 / 1.4)}>−</button>
        <span className={styles.lbScale}>{Math.round(t.scale * 100)}%</span>
        <button type="button" className={styles.lbBtn} title="Приблизить" onClick={() => zoomAt(1.4)}>+</button>
        <button type="button" className={styles.lbBtn} title={fitted ? 'Реальный размер (1:1)' : 'Вписать в экран'} onClick={() => toggleActualSize()}>
          {fitted ? '1:1' : '⤢'}
        </button>
        <button type="button" className={styles.lbBtn} title="Открыть оригинал в новой вкладке" onClick={openOriginal}>↗</button>
        <button type="button" className={styles.lbBtn} title="Закрыть (Esc)" aria-label="Закрыть" onClick={onClose}>✕</button>
      </div>

      {/* Галерея: стрелки и счётчик */}
      {gallery && (
        <>
          <button
            type="button"
            className={`${styles.lbBtn} ${styles.lbNav} ${styles.lbNavLeft}`}
            title="Предыдущее (←)"
            onClick={e => { e.stopPropagation(); goto(-1); }}
          >
            ‹
          </button>
          <button
            type="button"
            className={`${styles.lbBtn} ${styles.lbNav} ${styles.lbNavRight}`}
            title="Следующее (→)"
            onClick={e => { e.stopPropagation(); goto(1); }}
          >
            ›
          </button>
          <div className={styles.lbCounter} onClick={e => e.stopPropagation()}>
            {index + 1} / {gallery.length}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}

/**
 * Хук для открытия изображений в лайтбоксе. Возвращает функцию открытия и
 * готовый элемент лайтбокса — единый механизм для миниатюр вложений
 * (задачи/тикеты), чтобы они открывались попапом, а не новой вкладкой.
 * Второй аргумент open — список всех изображений блока (галерея ←/→).
 */
export function useImageLightbox(): { open: (src: string, list?: string[]) => void; element: React.ReactNode } {
  const [state, setState] = React.useState<{ src: string; list?: string[] } | null>(null);
  return {
    open:    React.useCallback((s: string, list?: string[]) => setState({ src: s, list }), []),
    element: state ? <ImageLightbox src={state.src} list={state.list} onClose={() => setState(null)} /> : null,
  };
}

// ── Компонент для отображения контента (HTML или legacy Markdown) ─────
export const MarkdownContent = React.memo(function MarkdownContent({
  text, className, style,
}: {
  text: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const html = React.useMemo(() => renderRich(text), [text]);
  const [preview, setPreview] = React.useState<{ src: string; list: string[] } | null>(null);

  // Клик по картинке в тексте — просмотр; все img блока попадают в галерею
  const handleClick = React.useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const src = (target as HTMLImageElement).src;
      if (src) {
        e.preventDefault();
        e.stopPropagation();
        const list = Array.from(e.currentTarget.querySelectorAll('img')).map(im => (im as HTMLImageElement).src);
        setPreview({ src, list });
      }
    }
  }, []);

  return (
    <>
      <div
        className={`${styles.editor} ${className ?? ''}`}
        style={style}
        onClick={handleClick}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {preview && <ImageLightbox src={preview.src} list={preview.list} onClose={() => setPreview(null)} />}
    </>
  );
});
